import { User } from '@auth0/auth0-react';
import axios, { AxiosInstance, CancelToken, CancelTokenSource } from 'axios';
import { getParsedAuth0Username } from '@Services/authentication/Helpers';
import { error } from '@Services/notification/NotificationServices';
import { IRequestOptions } from './Interfaces';

const singleton = Symbol();
const singletonEnforcer = Symbol();

export const AXIOS_CANCEL_ERROR_CODE = -999;

export class AxiosClient {
    _instance: AxiosInstance;
    _geoserverInstance: AxiosInstance;
    _tokenGenerator: any;
    _user: User;

    constructor(enforcer) {
        if (enforcer !== singletonEnforcer) {
            throw new Error("can't construct singleton");
        }

        this._instance = axios.create({
            baseURL: process.env.REQUEST_BASE_URI,
        });
        this._geoserverInstance = axios.create();

        this._instance.interceptors.request.use(
            // eslint-disable-next-line
            // @ts-ignore
            async (config) => {
                const token = await this.getToken();

                const headers: any = {
                    ...config.headers,
                    Authorization: `Bearer ${token}`,
                };

                return {
                    ...config,
                    headers: headers,
                };
            },
            (error) => {
                Promise.reject(error);
            }
        );

        this._geoserverInstance.interceptors.request.use(
            //eslint-disable-next-line
            // @ts-ignore
            async (config) => {
                const token = await this.getToken();

                const headers: any = {
                    ...config.headers,
                    Authorization: `Bearer ${token}`,
                    user_id: getParsedAuth0Username(this._user?.sub),
                };

                return {
                    ...config,
                    headers: headers,
                };
            },
            (error) => {
                Promise.reject(error);
            }
        );
    }

    static get instance(): AxiosClient {
        if (!this[singleton]) {
            this[singleton] = new AxiosClient(singletonEnforcer);
        }
        return this[singleton];
    }

    setTokenGenerator(tokenGenerator) {
        this._tokenGenerator = tokenGenerator;
        return this;
    }

    setUser(user) {
        this._user = user;
        return this;
    }

    getToken() {
        return this._tokenGenerator();
    }

    _onSuccess(response, startTime) {
        console.log(
            `${response.request.responseURL} ${(
                window.performance.now() - startTime
            ).toFixed(4)} ms`
        );
        return {
            isSuccess: true,
            message: null,
            statusCode: response.status,
            data: response.data,
        };
    }

    _onError(e) {
        if (e.response && e.response.data) {
            const errorMessage = Array.isArray(e.response.data)
                ? e.response.data.join(' - ')
                : JSON.stringify(e.response.data);

            error(errorMessage);

            return {
                isSuccess: false,
                message: errorMessage,
                statusCode: e.response.status,
                data: null,
            };
        } else if (axios.isCancel(e)) {
            return {
                isSuccess: false,
                statusCode: AXIOS_CANCEL_ERROR_CODE,
                data: null,
            };
        } else {
            const errorMessage = 'Cannot reach API server ' + e.url;
            error(errorMessage);
            return {
                isSuccess: false,
                message: errorMessage,
                statusCode: 500,
                data: null,
            };
        }
    }

    async get(url: string, data?: object, options?: IRequestOptions) {
        try {
            const { responseType, requestOptions } =
                destructureOptions(options);

            const startTime = window.performance.now();
            const response = await this._instance.get(url, {
                params: data,
                responseType: responseType,
                ...requestOptions,
            });
            return this._onSuccess(response, startTime);
        } catch (error) {
            return this._onError(error);
        }
    }

    async post(url: string, data?: object) {
        try {
            const startTime = window.performance.now();
            const response = await this._instance.post(url, data);
            return this._onSuccess(response, startTime);
        } catch (error) {
            return this._onError(error);
        }
    }

    async patch(url: string, data?: object) {
        try {
            const startTime = window.performance.now();
            const response = await this._instance.patch(url, data);
            return this._onSuccess(response, startTime);
        } catch (error) {
            return this._onError(error);
        }
    }

    async delete(url: string, data?: object) {
        try {
            const startTime = window.performance.now();
            const response = await this._instance.delete(url, data);
            return this._onSuccess(response, startTime);
        } catch (error) {
            return this._onError(error);
        }
    }

    async getGeoserverData(url, data?, cancelToken?: CancelToken) {
        try {
            const response = await this._geoserverInstance.get(url, {
                params: data,
                cancelToken: cancelToken,
            });
            return response;
        } catch (error) {
            return this._onError(error);
        }
    }
}

const destructureOptions = (
    options?: IRequestOptions
): { responseType: string; requestOptions?: any } => {
    if (!options) {
        return {
            responseType: 'json',
        };
    }
    const { responseType, ...requestOptions } = options;
    return {
        responseType: responseType ?? 'json',
        requestOptions: requestOptions,
    };
};
